import {
  AmountType,
  OrderTypes,
  PrecisionType,
  PrecisionConfig,
  QueryStatus,
  AvailableQuantityResponse,
  GetCreateSignalOrTradeGeneralDataResponse,
} from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import { useCallback, useContext, useEffect, useMemo } from "react";
import {
  TradeFormSetters,
  TradeFormValues,
} from "@shared/features/tradingTerminal/common/types";
import {
  convertAmountToQuantity,
  convertQuantityToAmount,
  getConversionMinCost,
} from "@shared/features/trades/common/utils";
import {
  getMinAndMaxPrices,
  validateNumLongShortTrades,
} from "@shared/features/tradingTerminal/common/utils";
import { getBotMinAmount } from "@shared/features/bots/BotForm/common/utils";
import { isNumber } from "lodash";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { getDefaultAmountPerTradeValue } from "@shared/common/utils";
import useBotAmount from "@shared/features/bots/BotForm/useBotAmount";
import { DEFAULT_AMOUNT_RATIO_FROM_AVAILABLE_AMOUNT } from "@shared/features/tradingTerminal/common/constants";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getAssetFromSymbol, getPairFromSymbol } from "@shared/utils/misc";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";

interface useTradingTerminalQueriesProps {
  formValues: TradeFormValues;
  formSetters: TradeFormSetters;
  isSignal?: boolean;
  isLoading?: boolean;
  getCreateGeneralDataQuery: QueryStatus<GetCreateSignalOrTradeGeneralDataResponse>;
  getAvailableQuantityQuery: QueryStatus<AvailableQuantityResponse>;
  currentPrice: number;
  isAdminWorkspace?: boolean;
}

const useAmount = ({
  formValues,
  formSetters,
  isSignal,
  isLoading,
  getCreateGeneralDataQuery,
  getAvailableQuantityQuery,
  currentPrice,
  isAdminWorkspace,
}: useTradingTerminalQueriesProps) => {
  const {
    symbols,
    quantity,
    extraInfo,
    account,
    entryOrders,
    amountInitialized,
    ordersInitialized,
  } = formValues;
  const {
    setAmount,
    setAmountPercent,
    setAmountInitialized,
    setQuantity,
    setQuantityPercent,
  } = formSetters;
  const { getExchangePriceFactorLimits } = useContext(
    ExchangesMetadataContext
  )!;
  const priceFactorLimits = getExchangePriceFactorLimits(account?.exchange);
  const createTradeGeneralData = getCreateGeneralDataQuery.data;
  const availableQuantityData = getAvailableQuantityQuery.data;
  const {
    getSymbolMetadata,
    getAmountConversionFormula,
    getQuantityConversionFormula,
  } = useSymbolsMetadata();
  const { data: amountData, status: amountDataStatus } = useBotAmount({
    account,
    symbol: symbols[0],
    skip: isSignal,
  });
  const loading = isLoading || amountDataStatus.isLoading;

  const symbolMetadata = getSymbolMetadata(symbols[0], account?.exchange);
  const amountConversionFormula = getAmountConversionFormula(
    symbols[0],
    account?.exchange,
    account
  );
  const quantityConversionFormula = getQuantityConversionFormula(
    symbols[0],
    account?.exchange,
    account
  );
  const minAmountLimit = symbolMetadata?.amount_min;
  const minQuantityLimit = symbolMetadata?.quantity_min;
  const entriesInitialized = ordersInitialized[OrderTypes.ENTRY];

  const { getPrecision } = usePrecisions();
  const usdAmountPrecision = getPrecision(PrecisionType.USD_AMOUNT);

  let precisionConfig = createTradeGeneralData?.precision;
  precisionConfig = {
    ...createTradeGeneralData?.precision,
    amount: amountData?.mainCoinAmountPrecision || 0,
    quantity: availableQuantityData?.precision?.quantity || 0,
    percent: (precisionConfig?.percent || 0) / 2,
  } as PrecisionConfig;

  const amountCoin =
    amountData?.mainCoin ?? getPairFromSymbol(symbols[0] || "");
  const quantityCoin =
    availableQuantityData?.coin ?? getAssetFromSymbol(symbols[0] || "");

  const availableUsdAmount = amountData?.availableAmount.USDAmount || 0;
  const availableMainCoinAmount = amountData?.availableAmount.mainCoin;
  const availableAmount = isSignal
    ? isAdminWorkspace
      ? 20
      : 100
    : toFixed(availableMainCoinAmount || 0, precisionConfig.amount);
  const rawAvailableQuantity = toFixed(
    availableQuantityData?.data?.balance?.quantity || 0,
    precisionConfig.quantity
  );

  const leverage = extraInfo?.leverage || 1;
  const generalInfo = createTradeGeneralData?.data?.general?.info;
  const canOpenBothDirections =
    createTradeGeneralData?.limits?.can_open_both_directions;

  const checkOppositePositionError = useCallback(
    (reverse: boolean) =>
      validateNumLongShortTrades(
        !!canOpenBothDirections,
        reverse,
        rawAvailableQuantity,
        generalInfo
      ),
    [canOpenBothDirections, rawAvailableQuantity, generalInfo]
  );

  let availableQuantity = Math.abs(rawAvailableQuantity);
  if (availableQuantity && checkOppositePositionError(!!extraInfo?.reverse)) {
    availableQuantity = 0;
  }

  const getMinAmountByPrice = useCallback(
    (price: number): number => {
      return getConversionMinCost(
        true,
        leverage,
        precisionConfig?.amount,
        precisionConfig?.quantity,
        price,
        amountConversionFormula,
        minAmountLimit,
        minQuantityLimit,
        quantityConversionFormula
      );
    },
    [
      leverage,
      precisionConfig?.amount,
      precisionConfig?.quantity,
      amountConversionFormula,
      quantityConversionFormula,
      minAmountLimit,
      minQuantityLimit,
    ]
  );

  const minQuantity = useMemo(
    () =>
      Math.abs(
        getConversionMinCost(
          false,
          leverage,
          precisionConfig?.amount,
          precisionConfig?.quantity,
          currentPrice,
          amountConversionFormula,
          minAmountLimit,
          minQuantityLimit
        ) || 0
      ),
    [
      leverage,
      precisionConfig?.amount,
      precisionConfig?.quantity,
      currentPrice,
      amountConversionFormula,
      minAmountLimit,
      minQuantityLimit,
    ]
  );

  const quantityAmount = useMemo(() => {
    const value = convertQuantityToAmount(
      Number(quantity),
      leverage,
      currentPrice,
      amountConversionFormula
    );
    return value ? toFixed(value, precisionConfig?.amount) : 0;
  }, [
    quantity,
    leverage,
    currentPrice,
    amountConversionFormula,
    precisionConfig?.amount,
  ]);

  const priceLimits = getMinAndMaxPrices(
    currentPrice,
    precisionConfig?.price || 0,
    priceFactorLimits
  );

  useEffect(() => {
    if (!isLoading) {
      setQuantity("0");
      setQuantityPercent("0");
    }
  }, [isLoading]);

  const parsedEntries = useMemo(() => {
    if (!getCreateGeneralDataQuery.isFetching && entriesInitialized) {
      return entryOrders.map((order) => {
        const price = parseFloat(order.price);
        return {
          price,
          ratio: order.percent ? parseFloat(order.percent) / 100 : 0,
          minAmount: getMinAmountByPrice(price),
        };
      });
    }
  }, [entryOrders, getMinAmountByPrice, entriesInitialized]);

  const validateOrderConvertedAmount = useCallback(
    (amount: number, price: number) => {
      if (!isNumber(minQuantityLimit)) return true;
      const orderQuantity =
        convertAmountToQuantity(
          amount,
          leverage,
          price,
          quantityConversionFormula
        ) || 0;
      return (
        toFixed(orderQuantity, precisionConfig?.quantity) >= minQuantityLimit
      );
    },
    [
      minQuantityLimit,
      leverage,
      precisionConfig.quantity,
      quantityConversionFormula,
    ]
  );

  const minMainCoinAmount = useMemo(() => {
    if (isSignal) return 0.01;
    if (entriesInitialized) {
      return getBotMinAmount(
        parsedEntries,
        undefined,
        undefined,
        precisionConfig?.amount || 0,
        account?.exchange,
        validateOrderConvertedAmount
      );
    }
  }, [
    parsedEntries,
    precisionConfig?.amount,
    account?.exchange,
    entriesInitialized,
  ]);

  const minUsdAmount = useMemo(() => {
    if (minMainCoinAmount) {
      return toFixed(
        amountData?.convertMainCoinAmountToUSD(minMainCoinAmount),
        usdAmountPrecision,
        true
      );
    }
  }, [amountData, minMainCoinAmount]);

  const defaultUsdAmount = useMemo(() => {
    if (minUsdAmount !== undefined && availableUsdAmount !== undefined) {
      return getDefaultAmountPerTradeValue(
        availableUsdAmount,
        minUsdAmount,
        AmountType.FIXED_USD_AMOUNT,
        undefined,
        usdAmountPrecision,
        false,
        DEFAULT_AMOUNT_RATIO_FROM_AVAILABLE_AMOUNT
      );
    }
  }, [availableUsdAmount, minUsdAmount, usdAmountPrecision]);

  const defaultAmount = useMemo(() => {
    if (
      defaultUsdAmount !== undefined &&
      availableMainCoinAmount !== undefined &&
      availableUsdAmount !== undefined
    ) {
      return (
        defaultUsdAmount *
        (availableMainCoinAmount /
          (availableUsdAmount > 0 ? availableUsdAmount : 1))
      );
    }
  }, [
    defaultUsdAmount,
    availableMainCoinAmount,
    availableUsdAmount,
    precisionConfig?.amount,
  ]);

  useEffect(() => {
    if (!isSignal) {
      setAmountInitialized(false);
    }
  }, [extraInfo?.reverse]);

  useEffect(() => {
    if (
      entriesInitialized &&
      !loading &&
      defaultAmount !== undefined &&
      !amountInitialized &&
      !isSignal
    ) {
      setAmount(defaultAmount.toString());
      setAmountPercent(
        toFixed(
          (toFixed(defaultAmount, precisionConfig?.amount) / availableAmount) *
            100,
          precisionConfig?.percent
        ).toString()
      );
      setAmountInitialized(true);
    }
  }, [
    loading,
    defaultAmount,
    availableAmount,
    amountInitialized,
    entriesInitialized,
    availableMainCoinAmount,
  ]);

  return {
    precisionConfig,
    amountCoin,
    quantityCoin,
    availableAmount,
    availableUsdAmount,
    minAmount: minMainCoinAmount,
    availableQuantity,
    minQuantity,
    quantityAmount,
    priceLimits,
    getMinAmountByPrice,
    checkOppositePositionError,
    convertMainCoinAmountToUSD: amountData?.convertMainCoinAmountToUSD,
    usdAmountPrecision,
  };
};

export default useAmount;
